import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import RichTextEditor from "../components/RichTextEditor";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = ["Workshop", "Seminar", "Competition", "Campus", "Networking"];
const MODES = ["Campus", "Online"];
const STATUSES = ["active", "draft"];

const emptyEvent = {
  title: "", subtitle: "", event_type: "Workshop", series_tag: "", date: "", time: "",
  location: "", mode: "Campus", audience: "", audience_tag: "", description: "", about: "",
  experiences: [{ icon: "bolt", title: "", description: "" }],
  speakers: [{ name: "", title: "", image: "", linkedin: "" }],
  banner_image: "", card_image: "", spots_left: 50, brochure_url: "", status: "active",
};

const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({ ...emptyEvent });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events`);
      setEvents(res.data);
    } catch (err) { console.error("Failed to fetch events:", err); }
    finally { setLoading(false); }
  };

  const seedEvents = async () => {
    try {
      await axios.post(`${API_URL}/api/events/seed`);
      fetchEvents();
    } catch (err) { console.error("Seed failed:", err); }
  };

  const handleEdit = (event) => {
    setEditingEvent(event.id);
    setFormData({
      ...event,
      experiences: event.experiences?.length ? event.experiences : [{ icon: "bolt", title: "", description: "" }],
      speakers: event.speakers?.length ? event.speakers : [{ name: "", title: "", image: "", linkedin: "" }],
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingEvent(null);
    setFormData({ ...emptyEvent });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API_URL}/api/events/${id}`);
      fetchEvents();
    } catch (err) { console.error("Delete failed:", err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent}`, formData);
      } else {
        await axios.post(`${API_URL}/api/events`, formData);
      }
      setShowForm(false);
      fetchEvents();
    } catch (err) { console.error("Save failed:", err); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const updateExperience = (index, field, value) => {
    const updated = [...formData.experiences];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, experiences: updated }));
  };

  const addExperience = () => setFormData(prev => ({ ...prev, experiences: [...prev.experiences, { icon: "bolt", title: "", description: "" }] }));
  const removeExperience = (i) => setFormData(prev => ({ ...prev, experiences: prev.experiences.filter((_, idx) => idx !== i) }));

  const updateSpeaker = (index, field, value) => {
    const updated = [...formData.speakers];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, speakers: updated }));
  };

  const addSpeaker = () => setFormData(prev => ({ ...prev, speakers: [...prev.speakers, { name: "", title: "", image: "", linkedin: "" }] }));
  const removeSpeaker = (i) => setFormData(prev => ({ ...prev, speakers: prev.speakers.filter((_, idx) => idx !== i) }));

  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-events-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl font-black text-[#2F4DC5]" data-testid="admin-logo">Bower Admin</Link>
            <span className="text-gray-400">|</span>
            <h1 className="text-lg font-semibold text-gray-800">Events Manager</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/events" className="text-sm text-blue-600 hover:underline" data-testid="admin-view-events-link">View Events Page</Link>
            <button onClick={handleNew} className="bg-[#2F4DC5] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#2540a0] transition-colors" data-testid="admin-new-event-btn">
              + New Event
            </button>
            <button onClick={seedEvents} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="admin-seed-btn">
              Seed Demo Data
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Event Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/50 overflow-y-auto" data-testid="admin-event-form-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 mb-10">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">{editingEvent ? "Edit Event" : "Create New Event"}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className={labelCls}>Title *</label>
                    <input className={inputCls} value={formData.title} onChange={e => updateField("title", e.target.value)} required data-testid="admin-input-title" />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Subtitle</label>
                    <input className={inputCls} value={formData.subtitle} onChange={e => updateField("subtitle", e.target.value)} data-testid="admin-input-subtitle" />
                  </div>
                  <div>
                    <label className={labelCls}>Event Type</label>
                    <select className={inputCls} value={formData.event_type} onChange={e => updateField("event_type", e.target.value)} data-testid="admin-select-type">
                      {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Series Tag</label>
                    <input className={inputCls} value={formData.series_tag} onChange={e => updateField("series_tag", e.target.value)} placeholder="e.g., Signature Series" />
                  </div>
                  <div>
                    <label className={labelCls}>Date</label>
                    <input className={inputCls} value={formData.date} onChange={e => updateField("date", e.target.value)} placeholder="e.g., Oct 24, 2024" data-testid="admin-input-date" />
                  </div>
                  <div>
                    <label className={labelCls}>Time</label>
                    <input className={inputCls} value={formData.time} onChange={e => updateField("time", e.target.value)} placeholder="e.g., 10:00 AM" />
                  </div>
                  <div>
                    <label className={labelCls}>Location</label>
                    <input className={inputCls} value={formData.location} onChange={e => updateField("location", e.target.value)} placeholder="e.g., Main Campus" data-testid="admin-input-location" />
                  </div>
                  <div>
                    <label className={labelCls}>Mode</label>
                    <select className={inputCls} value={formData.mode} onChange={e => updateField("mode", e.target.value)}>
                      {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Audience</label>
                    <input className={inputCls} value={formData.audience} onChange={e => updateField("audience", e.target.value)} placeholder="e.g., School, Founders, All" />
                  </div>
                  <div>
                    <label className={labelCls}>Audience Tag</label>
                    <input className={inputCls} value={formData.audience_tag} onChange={e => updateField("audience_tag", e.target.value)} placeholder="e.g., UG & Professionals" />
                  </div>
                  <div>
                    <label className={labelCls}>Spots Left</label>
                    <input type="number" className={inputCls} value={formData.spots_left} onChange={e => updateField("spots_left", parseInt(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className={labelCls}>Status</label>
                    <select className={inputCls} value={formData.status} onChange={e => updateField("status", e.target.value)} data-testid="admin-select-status">
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={labelCls}>Short Description (for card)</label>
                  <textarea className={inputCls} rows={2} value={formData.description} onChange={e => updateField("description", e.target.value)} data-testid="admin-input-description" />
                </div>
                <div>
                  <label className={labelCls}>About (full description)</label>
                  <RichTextEditor
                    value={formData.about}
                    onChange={(val) => updateField("about", val)}
                    placeholder="Write the full event description..."
                  />
                </div>

                {/* Images */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Card Image URL</label>
                    <input className={inputCls} value={formData.card_image} onChange={e => updateField("card_image", e.target.value)} placeholder="https://..." />
                  </div>
                  <div>
                    <label className={labelCls}>Banner Image URL</label>
                    <input className={inputCls} value={formData.banner_image} onChange={e => updateField("banner_image", e.target.value)} placeholder="https://..." />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Brochure URL</label>
                    <input className={inputCls} value={formData.brochure_url} onChange={e => updateField("brochure_url", e.target.value)} placeholder="https://..." />
                  </div>
                </div>

                {/* Experiences */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className={labelCls}>Experiences</label>
                    <button type="button" onClick={addExperience} className="text-sm text-blue-600 hover:underline">+ Add</button>
                  </div>
                  {formData.experiences.map((exp, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-start">
                      <input className={`${inputCls} col-span-2`} value={exp.icon} onChange={e => updateExperience(i, "icon", e.target.value)} placeholder="Icon" />
                      <input className={`${inputCls} col-span-3`} value={exp.title} onChange={e => updateExperience(i, "title", e.target.value)} placeholder="Title" />
                      <input className={`${inputCls} col-span-6`} value={exp.description} onChange={e => updateExperience(i, "description", e.target.value)} placeholder="Description" />
                      <button type="button" onClick={() => removeExperience(i)} className="col-span-1 text-red-400 hover:text-red-600 text-center pt-2">&times;</button>
                    </div>
                  ))}
                </div>

                {/* Speakers */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className={labelCls}>Speakers</label>
                    <button type="button" onClick={addSpeaker} className="text-sm text-blue-600 hover:underline">+ Add</button>
                  </div>
                  {formData.speakers.map((sp, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-start">
                      <input className={`${inputCls} col-span-3`} value={sp.name} onChange={e => updateSpeaker(i, "name", e.target.value)} placeholder="Name" />
                      <input className={`${inputCls} col-span-3`} value={sp.title} onChange={e => updateSpeaker(i, "title", e.target.value)} placeholder="Title" />
                      <input className={`${inputCls} col-span-3`} value={sp.image} onChange={e => updateSpeaker(i, "image", e.target.value)} placeholder="Image URL" />
                      <input className={`${inputCls} col-span-2`} value={sp.linkedin} onChange={e => updateSpeaker(i, "linkedin", e.target.value)} placeholder="LinkedIn" />
                      <button type="button" onClick={() => removeSpeaker(i)} className="col-span-1 text-red-400 hover:text-red-600 text-center pt-2">&times;</button>
                    </div>
                  ))}
                </div>

                {/* Save */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
                  <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#2F4DC5] hover:bg-[#2540a0] disabled:opacity-50" data-testid="admin-save-event-btn">
                    {saving ? "Saving..." : (editingEvent ? "Update Event" : "Create Event")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Events Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20" data-testid="admin-empty-state">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-4xl text-gray-400">event</span>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No events yet</h3>
            <p className="text-gray-500 mb-6">Create your first event or seed demo data.</p>
            <div className="flex justify-center gap-3">
              <button onClick={handleNew} className="bg-[#2F4DC5] text-white px-5 py-2.5 rounded-lg text-sm font-semibold">+ Create Event</button>
              <button onClick={seedEvents} className="bg-gray-100 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold">Seed Demo Data</button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="admin-events-table">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">{events.length} Events</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
                  <th className="px-6 py-3">Event</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Spots</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`admin-event-row-${event.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 text-sm">{event.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{event.location}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700">{event.event_type}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{event.date || "TBD"}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{event.mode}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${event.status === "active" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                        {event.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{event.spots_left}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/events/${event.id}`} className="text-xs text-blue-600 hover:underline" target="_blank">View</Link>
                        <button onClick={() => handleEdit(event)} className="text-xs text-gray-600 hover:text-blue-600" data-testid={`admin-edit-${event.id}`}>Edit</button>
                        <button onClick={() => handleDelete(event.id)} className="text-xs text-red-500 hover:text-red-700" data-testid={`admin-delete-${event.id}`}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEventsPage;
